import React, { FC, memo, useState, useMemo, useContext } from "react";
import {
  Box,
  Flex,
  Radio,
  RadioGroup,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import MediaModal from "@modules/Layout/components/MediaModal";
import ResourceCardItem from "./ResourceCardItem";
import {
  IResourceLabelType,
  ResourceType,
  ITypeOfResourceArr,
} from "../models/type";
import { ResourceFilterContext } from "../context";
import ResponsiveContainer from "@modules/Layout/components/ResponsiveContainer";
import useMediaModal from "../hooks/useMediaModal";
import Pagination from "rc-pagination";
import "rc-pagination/assets/index.css";
import { ResourceItem } from "@types/support";

interface Props {
  resources: ResourceItem[];
}

const ITEMS_PER_PAGE = 9;

const Category: React.FC<Props> = ({ resources }) => {
  let typeOfResource = useContext(ResourceFilterContext);
  if (!ITypeOfResourceArr.includes(typeOfResource)) {
    typeOfResource = "all";
  }
  const [resourceType, setResourceType] = useState<IResourceLabelType>(
    typeOfResource as IResourceLabelType
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { isOpen, handleOpenModal, handleCloseModal, mediaType, mediaUrl } =
    useMediaModal();

  const filteredResources = useMemo(() => {
    return resourceType === "all"
      ? resources
      : resources.filter(r => r.type === resourceType);
  }, [resources, resourceType]);

  const paginatedResources = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredResources.slice(startIndex, endIndex);
  }, [filteredResources, currentPage]);

  const handleChangeResourceType = (value: IResourceLabelType) => {
    setResourceType(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <ResponsiveContainer>
      <Box py={7}>
        <Box>
          <RadioGroup
            onChange={handleChangeResourceType}
            value={resourceType}
            mt={10}
          >
            <Flex direction="row" overflowX="auto" py={7}>
              {ResourceType.map(item => (
                <Radio
                  colorScheme="blue"
                  size="lg"
                  key={item.value}
                  value={item.value}
                >
                  <Text
                    textStyle={"hind_m"}
                    fontWeight={500}
                    color={"misc750"}
                    mr={4}
                  >
                    {item.label}
                  </Text>
                </Radio>
              ))}
            </Flex>
          </RadioGroup>

          <SimpleGrid
            columns={{ base: 1, md: 2, lg: 3 }}
            spacing={{ base: 4, lg: 7 }}
            my={10}
          >
            {paginatedResources.map((item, index) => (
              <Box key={index}>
                <ResourceCardItem
                  item={item}
                  isOpen={isOpen}
                  handleCloseModal={handleCloseModal}
                  handleOpenModal={handleOpenModal}
                />
              </Box>
            ))}
          </SimpleGrid>

          <MediaModal
            type={mediaType}
            url={mediaUrl}
            isOpen={isOpen}
            handleCloseModal={handleCloseModal}
          />
        </Box>
        <Box>
          {filteredResources.length > ITEMS_PER_PAGE && (
            <Box mb={12}>
              <Pagination
                className="p2"
                total={filteredResources.length}
                pageSize={ITEMS_PER_PAGE}
                current={currentPage}
                onChange={handlePageChange}
                hideOnSinglePage
              />
            </Box>
          )}
        </Box>
      </Box>
    </ResponsiveContainer>
  );
};

export default memo(Category);
